import * as logger from "./logger";
import * as notify from "../shared/notify";
import eventEmitter from "./events";
import { loadFromCache } from "./cache-service";
import { globalEnvironmentConfig } from "global-environment-config";

const currentEnv = process.env.NODE_ENV;
const API_TARGET = process.env.REACT_APP_API_TARGET || "local";

export const isProdEnv = () => currentEnv === "production" && window.location.hostname === "intake.syndigo.com";

export const isDevEnv = () => window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

export const isDev = currentEnv === "development" || currentEnv === "jsdom";
export const useMock = (currentEnv === "local" || currentEnv === "jsdom") && false;
export const EVENT_AJAXING = "EVENT_AJAXING";
export const EVENT_DONE_AJAXING = "EVENT_DONE_AJAXING";

const fetchSettings = {
    passthrough: false,
    defaultReturn: null,
};

const getToken = () => {
    return API_TARGET === "local" ? localStorage.getItem("access_token") : process.env.REACT_APP_API_TOKEN;
};

const buildMockUrl = (endpoint) => {
    return `/mock${endpoint}`;
};

const buildUrl = (endpoint) => {
    return isDev ? `/api${endpoint}` : `${globalEnvironmentConfig.ApiBaseUrl}/api${endpoint}`;
};

async function handleJsonResponse(response, userSettings, endpoint = "") {
    eventEmitter.emit(EVENT_DONE_AJAXING);

    const settings = { ...fetchSettings, ...userSettings };

    let body = null;
    try {
        body = await response.text().then((text) => (text.length ? JSON.parse(text) : null));
    } catch {}

    // TODO: this is implemented incorrectly!
    if (notify.errorResponse(body, settings.passthrough, settings.displayError)) {
        return settings.passthrough ? body : settings.defaultReturn;
    }

    if (response.status === 401) {
        notify.error("You do not have the permissions to perform this action.");
        logger.error("You do not have the permissions to perform this action.");

        if (!settings.noRedirect) {
            window.location = endpoint === "/app-settings" ? "/logout" : "/";
        }

        return settings.defaultReturn;
    }

    if (response.status === 503) {
        notify.error("Api is unavailable");
        logger.error("Api is unavailable");

        return settings.defaultReturn;
    }

    if (response.status === 500) {
        notify.error("Internal Server Error");
        logger.error("Internal Server Error");

        return settings.defaultReturn;
    }

    if (response.status === 403) {
        notify.error("You do not have the permissions to perform this action.");
        logger.error("You do not have the permissions to perform this action.");

        return settings.defaultReturn;
    }

    if (response.status === 404) {
        notify.error("404 Not Found");
        logger.error("404 Not Found");

        return settings.defaultReturn;
    }

    if (response.status > 400) {
        notify.error("Bad response from server");
        logger.error("Bad response from server");

        return settings.defaultReturn;
    }

    if (response.status === 204) {
        return settings.defaultReturn || { success: true };
    }

    return body !== null ? body : true;
}

async function handleMultipartResponse(response) {
    const body = await response.text().then((text) => (text.length ? JSON.parse(text) : null));

    if (notify.errorResponse(body)) {
        return null;
    }

    if (response.status === 403) {
        notify.error("You do not have the permissions to perform this action.");
        logger.error("You do not have the permissions to perform this action.");

        return null;
    }

    if (response.status === 413) {
        notify.error("File is too large");
        throw new Error("File is too large");
    }

    if (response.status > 400) {
        notify.error("Bad response from server");
        throw new Error("Bad response from server");
    }

    if (response.status === 204) {
        return { success: true };
    }

    return body;
}

export const get = async (endpoint, settings = {}) => {
    eventEmitter.emit(EVENT_AJAXING);
    const url = settings.useMock ? buildMockUrl(endpoint) : buildUrl(endpoint);
    logger.log(`GET ${url}`);

    return fetch(url, {
        method: "GET", // *GET, POST, PUT, DELETE, etc.
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
        },
    }).then((res) => handleJsonResponse(res, settings, endpoint));
};

const getFileName = (contentDisposition, defaultName) => {
    const match = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/i);

    return match ? match[1] : defaultName || "export.csv";
};

export const getCSV = async (endpoint, settings = {}) => {
    eventEmitter.emit(EVENT_AJAXING);
    const url = settings.useMock ? buildMockUrl(endpoint) : buildUrl(endpoint);
    logger.log(`GET ${url}`);

    return fetch(url, {
        method: "GET", // *GET, POST, PUT, DELETE, etc.
        headers: {
            "Content-Type": "text/csv",
            Authorization: `Bearer ${getToken()}`,
        },
    })
        .then(async (response) => {
            if (!response.ok) {
                return false;
            }

            const contentDisposition = response.headers.get("content-disposition");
            const filename = contentDisposition ? getFileName(contentDisposition, settings.fileName) : settings.fileName || "export.csv";
            const text = await response.text();

            return { filename, text };
        })
        .then((responseText) => {
            if (!responseText) {
                return false;
            }

            const newUrl = window.URL.createObjectURL(new Blob([responseText.text]));
            const link = document.createElement("a");
            link.href = newUrl;
            link.setAttribute("download", `${responseText.filename}`);
            document.body.appendChild(link);

            link.click();

            link.parentNode.removeChild(link);

            return true;
        });
};

export const getFromCache = async (endpoint, settings = {}) => {
    return loadFromCache(`get_${endpoint}`, () => get(endpoint, settings));
};

export const getImageStream = (endpoint) => {
    logger.log(`GET ${endpoint}`);

    return fetch(buildUrl(endpoint), {
        method: "GET", // *GET, POST, PUT, DELETE, etc.
        headers: {
            Authorization: `Bearer ${getToken()}`,
        },
    })
        .then((r) => {
            return r.blob();
        })
        .then((blob) => {
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => {
                    resolve(reader.result);
                };
                reader.readAsDataURL(blob);
            });
        });
};

export const downloadImage = async (endpoint, sourcePage, hasParams) => {
    const params = hasParams ? `&sourcePage=${sourcePage}` : sourcePage ? `?sourcePage=${sourcePage}` : "";
    eventEmitter.emit(EVENT_AJAXING);
    const url = buildUrl(endpoint + params);
    logger.log(`GET ${url}`);

    return fetch(url, {
        method: "GET", // *GET, POST, PUT, DELETE, etc.
        headers: {
            Authorization: `Bearer ${getToken()}`,
        },
    }).then((responseText) => {
        if (!responseText || !responseText.ok) {
            return false;
        }

        const link = document.createElement("a");
        link.href = buildUrl(endpoint);
        link.setAttribute("download", "image");
        document.body.appendChild(link);

        link.click();

        link.parentNode.removeChild(link);

        return true;
    });
};

// TODO: See what fetch settings/headers can be pulled out and shared between POST/PUT/DELETE
export const post = (endpoint, payload, settings = {}) => {
    logger.log(`POST ${endpoint} ${JSON.stringify(payload)}`);

    return fetch(buildUrl(endpoint), {
        body: JSON.stringify(payload),
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
        },
        method: "POST", // *GET, POST, PUT, DELETE, etc.
        //redirect: 'follow',
        //credentials: 'include',
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
    }).then((res) => handleJsonResponse(res, settings));
};

export const deleteWithPayload = (endpoint, payload, settings = {}) => {
    logger.log(`DELETE ${endpoint} ${JSON.stringify(payload)}`);

    return fetch(buildUrl(endpoint), {
        body: JSON.stringify(payload),
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
        },
        method: "DELETE", // *GET, POST, PUT, DELETE, etc.
        //redirect: 'follow',
        //credentials: 'include',
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
    }).then((res) => handleJsonResponse(res, settings));
};

export const postMultiPart = (endpoint, formData, settings = {}) => {
    logger.log(`POST MULTIPART ${endpoint}`);

    return fetch(buildUrl(endpoint), {
        body: formData,
        headers: {
            Authorization: `Bearer ${getToken()}`,
        },
        method: "POST", // *GET, POST, PUT, DELETE, etc.
        //redirect: 'follow',
        //credentials: 'include',
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
    }).then((res) => handleJsonResponse(res, settings));
};

export const putMultiPart = (endpoint, formData) => {
    logger.log(`PUT MULTIPART ${endpoint}`);

    return fetch(buildUrl(endpoint), {
        body: formData,
        headers: {
            Authorization: `Bearer ${getToken()}`,
        },
        method: "PUT", // *GET, POST, PUT, DELETE, etc.
        //redirect: 'follow',
        //credentials: 'include',
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
    }).then(handleMultipartResponse);
};

export const put = (endpoint, payload, settings = {}) => {
    logger.log(`PUT ${endpoint} ${JSON.stringify(payload)}`);

    return fetch(buildUrl(endpoint), {
        body: JSON.stringify(payload),
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
        },
        method: "PUT", // *GET, POST, PUT, DELETE, etc.
        //redirect: 'follow',
        //credentials: 'include',
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
    }).then((res) => handleJsonResponse(res, settings));
};

export const patch = (endpoint, payload, settings = {}) => {
    logger.log(`PATCH ${endpoint} ${JSON.stringify(payload)}`);

    return fetch(buildUrl(endpoint), {
        body: JSON.stringify(payload),
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
        },
        method: "PATCH", // *GET, POST, PUT, DELETE, etc.
        //redirect: 'follow',
        //credentials: 'include',
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
    }).then((res) => handleJsonResponse(res, settings));
};

export const deleteRequest = (endpoint, settings = {}) => {
    logger.log(`DELETE ${endpoint}`);

    return fetch(buildUrl(endpoint), {
        method: "DELETE", // *GET, POST, PUT, DELETE, etc.
        //redirect: 'follow',
        //credentials: 'include',
        headers: {
            Authorization: `Bearer ${getToken()}`,
        },
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
    }).then((res) => handleJsonResponse(res, settings));
};

export const getFileStream = (endpoint) => {
    logger.log(`GET Unedited Image Stream: ${endpoint}`);

    return fetch(buildUrl(endpoint), {
        method: "GET",
        headers: {
            responseType: "blob",
            Authorization: `Bearer ${getToken()}`,
        },
    }).then((r) => {
        return r.blob();
    });
};

export const patchMultiPart = (endpoint, formData) => {
    logger.log(`PATCH MULTIPART ${endpoint}`);

    return fetch(buildUrl(endpoint), {
        body: formData,
        headers: {
            Authorization: `Bearer ${getToken()}`,
        },
        method: "PATCH", // *GET, POST, PUT, DELETE, etc.
        //redirect: 'follow',
        //credentials: 'include',
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
    }).then(handleMultipartResponse);
};

export const hasErrorResponse = (res) => {
    if (Array.isArray(res)) {
        return res.some(({ failed }) => failed);
    }

    return false;
};
